import React, { type ReactNode, useCallback, useMemo, useRef } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type { Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useCSVDownloader } from 'react-papaparse';

import GroupAdd from 'assets/icons/icn _ 20px _ group-add.svg?react';
import DownloadIcon from 'assets/icons/icn _ 24px _ download.svg?react';
import EmailIcon from 'assets/icons/icn glyph 24_email.svg?react';
import SMSIcon from 'assets/icons/icn glyph 24_SMS.svg?react';
import { useStyles as usePlayerStyles } from 'components/tables/players/styles';
import { getEnvConfig } from 'config/environment';
import { useAddToEventDialog } from 'containers/dialogs/AddToEvent';
import { useAddToTeamDialog } from 'containers/dialogs/AddToTeam';
import { useEmailDialog } from 'containers/dialogs/SendEmail';
import { useSmsDialog } from 'containers/dialogs/Sms';
import type { ParticipantInterface } from 'typedefs/store/tmxTypes';

import { useStyles } from './styles';
import { exportPlayersToCSV } from './utils';

interface PlayerBulkActionsProps<T extends Record<string, any>> {
  selectedFlatRows: Row<T>[];
  showTeams: boolean;
  teams?: ParticipantInterface[];
  onResetRowSelection?: () => void;
}

const PlayerBulkActions: React.FC<React.PropsWithChildren<PlayerBulkActionsProps<ParticipantInterface>>> = ({
  selectedFlatRows,
  showTeams,
  teams,
  onResetRowSelection,
}) => {
  const { t } = useTranslation();
  const { classes: playerClasses } = usePlayerStyles();
  const { classes } = useStyles();
  const { CSVDownloader } = useCSVDownloader();

  const showSmsDialog = useSmsDialog();
  const showEmailDialog = useEmailDialog();
  const showAddToEventDialog = useAddToEventDialog();
  const showAddToTeamDialog = useAddToTeamDialog();

  const selected = useRef<ParticipantInterface[]>([]);
  selected.current = selectedFlatRows.map((row) => row.original);

  const handleOpenSMSDialog = useCallback(() => {
    showSmsDialog(selected.current);
  }, [showSmsDialog]);

  const handleOpenAddToEvent = useCallback(() => {
    showAddToEventDialog(selected.current, { onResetRowSelection });
  }, [onResetRowSelection, showAddToEventDialog]);

  const handleOpenAddToTeam = useCallback(() => {
    showAddToTeamDialog(selected.current, showTeams, undefined, onResetRowSelection);
  }, [onResetRowSelection, showAddToTeamDialog, showTeams]);

  const handleOpenEmailDialog = useCallback(() => {
    showEmailDialog(selected.current);
  }, [showEmailDialog]);

  const selectedParticipantValues = useMemo(
    () =>
      selectedFlatRows?.map((row) => ({
        name: row.getValue('name'),
        firstName: row.getValue('firstName'),
        lastName: row.getValue('lastName'),
        gender: row.getValue('gender'),
        events: row.getValue('events'),
        city: row.getValue('city'),
        state: row.getValue('state'),
        teams: row.getValue('teams'),
        signedIn: row?.getValue('signedIn'),
        status: row.getValue('status'),
        penalty: row.getValue('penalty'),
        organisationId: row.getValue('organisationId'),
        wtnSingles: row.getValue('scaling_wtn singles'),
        wtnDoubles: row.getValue('scaling_wtn doubles'),
      })),
    [selectedFlatRows],
  );

  if (!selectedFlatRows.length) return null;

  return (
    <Grid item className={playerClasses.bulkSelectActionContainer}>
      <Grid container direction="row" spacing={2}>
        <Grid item>
          <Typography className={playerClasses.selectedTypography}>
            {selectedFlatRows.length} {t('scted')}:
          </Typography>
        </Grid>
        <Grid item className={playerClasses.bulkSelectActions}>
          <Grid container alignItems="center" direction="row" spacing={2}>
            {getEnvConfig().REACT_APP_DISABLE_CUSTOM_EMAIL !== 'true' && (
              <ActionItem icon={<EmailIcon />} label={t('sendEmail')} onClick={handleOpenEmailDialog} />
            )}
            <ActionItem icon={<SMSIcon />} label={t('sendSMS')} onClick={handleOpenSMSDialog} />
            <ActionItem icon={<GroupAdd />} label={t('addToEvent')} onClick={handleOpenAddToEvent} />
            {showTeams && <ActionItem icon={<GroupAdd />} label={t('addToTeam')} onClick={handleOpenAddToTeam} />}
            <ActionItem icon={<DownloadIcon className={classes.downloadIcon} />}>
              <CSVDownloader bom data={exportPlayersToCSV(selectedParticipantValues, teams, t)} filename="players">
                {t('exportToCSV')}
              </CSVDownloader>
            </ActionItem>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface ActionItemProps {
  icon: ReactNode;
  onClick?: () => void;
  label?: string;
}

const ActionItem: React.FC<React.PropsWithChildren<ActionItemProps>> = React.memo(
  ({ icon, onClick, label, children }) => {
    const { classes } = useStyles();
    return (
      <Grid item>
        <Grid container direction="row" spacing={1}>
          <Grid item className={classes.bulkIconGridItem}>
            {icon}
          </Grid>
          <Grid item onClick={onClick}>
            {children}
            {!!label && <Typography>{label}</Typography>}
          </Grid>
        </Grid>
      </Grid>
    );
  },
);

export default PlayerBulkActions;
